// DEEP SEARCH OVER LEADS — one definition of "does this row match?", shared by
// the leads page filter, the global search bar and the chat's retrieval step.
//
// WHY ONE PLACE. Each surface used to search a hand-picked handful of columns
// (name, city, trade, service area, the first contact's name and email). A
// person who is only in the packed `contacts` blob — the fourth contact at the
// company, say — was invisible to all three: typing their name into the leads
// page found nothing, the top bar found nothing, and the chat said it had "no
// specific details" about someone whose card was on the lead's own detail
// page. Every surface now searches the WHOLE record: every text column the row
// carries, plus every contact parsed out of the packed blob (name, title,
// email, phone, location, LinkedIn), plus the enrichment dossier when this
// browser holds one. Add a column to the import and it is searchable here
// without another edit.
//
// Two operations, both pure:
//   leadMatches(row, needle)         substring — the filter the leads page and
//                                    the top bar apply as the user types
//   retrieveLeads(rows, question)    ranked — the handful of records most
//                                    relevant to a chat question, with the
//                                    fields the model may quote
import { keyOf } from './companyMeta.js'
import { getEnrichment } from './enrichment.js'

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

// Apostrophes, the same way on both sides of a comparison: a possessive
// ("Yishai's", "Chicago Masonry's") loses its suffix, any other apostrophe
// ("O'Brien") is dropped, so "who's o'brien" and "name=Tim O'Brien" agree.
const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[’']s\b/g, '')
    .replace(/[’']/g, '')

/**
 * The packed `contacts` field, parsed: a flat run of `name=…; role=…; phone=…`
 * groups where a new `name=` starts a new contact. Enrichment appends
 * apollo_title / apollo_email / apollo_linkedin / apollo_location to the same
 * format — every key is kept as-is, and the readable ones are also exposed
 * under plain names (title, email, location, linkedin) so callers don't need to
 * know which enrichment wrote them.
 */
export function parsePackedContacts(raw) {
  const out = []
  let cur = null
  for (const tok of String(raw || '').split(';')) {
    const eq = tok.indexOf('=')
    if (eq === -1) continue
    const key = tok.slice(0, eq).trim()
    const val = tok.slice(eq + 1).trim()
    if (!key) continue
    if (key === 'name') {
      cur = { name: val }
      out.push(cur)
    } else if (cur) {
      cur[key] = val
    }
  }
  return out
    .filter((c) => c.name)
    .map((c) => ({
      name: c.name,
      title: c.apollo_title || c.title || c.role || '',
      email: c.apollo_email || c.email || '',
      phone: c.phone || c.apollo_phone || '',
      location: c.apollo_location || c.location || '',
      linkedin: c.apollo_linkedin || c.linkedin || '',
    }))
}

// Fields that are never worth searching: internal annotations and pure ids.
const SKIP_FIELD = /^_|^(company_id|profile_link|scrape_date|listed_since)$/

/**
 * Everything searchable about one row, lowercased, as one string. Every text
 * column the row carries — whatever the import happened to have — plus the
 * parsed contacts. Cached on the row under `_hay` so a 1,400-row scan per
 * keystroke stays a substring test per row.
 */
export function leadHaystack(row) {
  if (!row || typeof row !== 'object') return ''
  if (typeof row._hay === 'string') return row._hay
  const parts = []
  for (const [k, v] of Object.entries(row)) {
    if (SKIP_FIELD.test(k)) continue
    if (typeof v === 'string' || typeof v === 'number') parts.push(String(v))
  }
  for (const c of parsePackedContacts(row.contacts)) {
    parts.push(c.name, c.title, c.email, c.phone, c.location)
  }
  const hay = parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
  try {
    // Non-enumerable, so the row still serializes / compares as it always did.
    Object.defineProperty(row, '_hay', { value: hay, enumerable: false, configurable: true, writable: true })
  } catch {
    /* frozen row — recompute next time */
  }
  return hay
}

/** Substring match over the whole record. Empty needle matches everything. */
export function leadMatches(row, needle) {
  const q = String(needle || '').trim().toLowerCase()
  if (!q) return true
  return leadHaystack(row).includes(q)
}

// ── ranked retrieval for the chat ───────────────────────────────────────────

// Words that carry no signal about WHICH lead the user means.
const STOP = new Set(
  (
    'a an the and or of at in on to for from with by about is are was were be been am do does did ' +
    'who whos whose what which where when why how tell me show find give get list any some all this that ' +
    'these those it its their there here they them he she him her his hers we us our you your i my ' +
    'company companies lead leads contact contacts person people someone anyone works work working ' +
    'email phone number please can could would should will up out into over under vs versus'
  ).split(' '),
)

function tokens(question) {
  return [
    ...new Set(
      norm(question)
        .split(/[^a-z0-9@.+-]+/)
        .map((t) => t.replace(/^[.-]+|[.-]+$/g, ''))
        .filter((t) => t.length >= 2 && !STOP.has(t)),
    ),
  ]
}

/**
 * Whether a question carries any lead-shaped token at all — false for small
 * talk and stop-word-only questions, so callers can skip the mirror entirely.
 */
export function hasLeadTokens(question) {
  return tokens(question).length > 0
}

/**
 * Score one row against the question. Company name is the strongest signal, a
 * contact's name next, then emails, then anything else on the record. A
 * question that contains the company's full name (or a contact's full name)
 * outranks a stray token match by a wide margin, so "who's yishai at chicago
 * masonry" finds Chicago Masonry Construction over every other Chicago row.
 */
function scoreRow(row, q, toks) {
  const name = norm(clean(row.name))
  const contacts = parsePackedContacts(row.contacts)
  const hay = norm(leadHaystack(row))
  let score = 0
  const hits = []
  if (name && q.includes(name)) {
    score += 30
    hits.push('company')
  }
  for (const c of contacts) {
    const full = norm(clean(c.name))
    if (full && q.includes(full)) {
      score += 25
      hits.push(`contact:${c.name}`)
    }
  }
  const nameToks = name.split(/[^a-z0-9]+/).filter(Boolean)
  // A run of two name words in order ("chicago masonry" for "Chicago Masonry
  // Construction") is nearly the full name — worth far more than the words
  // scattered anywhere in the question.
  // Whole words only: "p masonry" must not match inside "drp masonry".
  const qWords = ` ${q.replace(/[^a-z0-9@.+-]+/g, ' ')} `
  for (let i = 0; i + 1 < nameToks.length; i += 1) {
    if (qWords.includes(` ${nameToks[i]} ${nameToks[i + 1]} `)) {
      score += 12
      hits.push(`name:${nameToks[i]} ${nameToks[i + 1]}`)
      break
    }
  }
  // Split on whitespace too, so a hyphenated name ("Anne-Marie") survives as
  // one token and matches the question's "anne-marie".
  const contactToks = contacts
    .flatMap((c) => {
      const n = norm(clean(c.name))
      return [...n.split(/[^a-z0-9]+/), ...n.split(/\s+/)]
    })
    .filter(Boolean)
  const emails = [clean(row.email).toLowerCase(), ...contacts.map((c) => clean(c.email).toLowerCase())].filter(Boolean)
  for (const t of toks) {
    if (nameToks.includes(t)) {
      score += 6
      hits.push(`name:${t}`)
      continue
    }
    if (contactToks.includes(t)) {
      score += 8
      hits.push(`contact:${t}`)
      continue
    }
    if (emails.some((e) => e === t || e.split('@')[0] === t)) {
      score += 8
      hits.push(`email:${t}`)
      continue
    }
    if (hay.includes(t)) {
      // An identifier-shaped token — a phone number, a domain, a hyphenated
      // name — pins down a record on its own; a plain word does not.
      const specific = /\d{3,}|\.[a-z]{2,}$|-/.test(t)
      score += specific ? 8 : 1
      hits.push(specific ? `field:${t}` : t)
    }
  }
  return { score, hits }
}

/**
 * The compact, quotable record for one lead: what the model may say about the
 * company and the people at it. Every value is one the row (or this browser's
 * copy of the enrichment dossier) actually holds; nothing is derived here.
 */
export function leadFacts(row, { research = null, hits = [] } = {}) {
  // Bounded per field: title/location are Apollo (self-declared) text, not
  // something the operator wrote.
  const cp = (v) => clean(v).slice(0, 120)
  const contacts = parsePackedContacts(row.contacts).map((c) => {
    const out = { name: cp(c.name) }
    if (c.title) out.title = cp(c.title)
    if (c.email) out.email = cp(c.email)
    if (c.phone) out.phone = cp(c.phone)
    if (c.location) out.location = cp(c.location)
    return out
  })
  const pick = (k) => {
    const v = clean(row[k])
    return v && !/^(unknown|n\/a|none)\.?$/i.test(v) ? v.slice(0, 220) : ''
  }
  const key = String(keyOf(row) || '')
  const out = {
    company: clean(row.name),
    lead_key: key,
    // THE IN-APP LEAD PAGE — the only thing the model may call "the lead
    // page". `website` below is the company's own site; without this path the
    // model linked THAT as the lead page and sent the rep out of the app.
    lead_page: key ? `/leads/company/${encodeURIComponent(key)}` : '',
    category: row._category || '',
    region: row._region || row.region || '',
    city_state: pick('city_state'),
    trade: pick('trade'),
    website: pick('website'),
    phone: pick('phone'),
    email: pick('email'),
    contact_name: pick('contact_name'),
    contact_role: pick('contact_role'),
    contacts,
    owners: pick('owners'),
    estimators: pick('estimators'),
    has_estimator: pick('has_estimator'),
    other_decision_makers: pick('other_decision_makers'),
    company_size: pick('company_size'),
    annual_volume: pick('annual_volume'),
    latest_revenue: pick('latest_revenue'),
    commercial_residential: pick('commercial_residential'),
    service_area: pick('service_area'),
    score: row.score ?? row.lead_score ?? '',
    score_reasoning: pick('score_reasoning'),
    sales_angles: pick('sales_angles'),
    objections: pick('objections'),
    matched_on: hits.slice(0, 8),
  }
  // The AI research dossier, when this browser has it — the same fields the
  // lead page shows. Bounded per field; never the whole thing.
  const r = research && typeof research === 'object' ? research : null
  if (r) {
    const rp = (k) => {
      const v = r[k]
      const s = Array.isArray(v) ? v.map(clean).filter(Boolean).join('; ') : clean(v)
      return s && !/^unknown\.?$/i.test(s) ? s.slice(0, 300) : ''
    }
    out.research = Object.fromEntries(
      [
        ['recent_activity', rp('recent_activity')],
        ['current_solution', rp('current_solution')],
        ['buying_signals', rp('buying_signals')],
        ['offering_mix', rp('offering_mix')],
        ['market_footprint', rp('market_footprint')],
        ['decision_makers', rp('decision_makers')],
        ['owners', rp('owners')],
        ['pros', rp('pros')],
        ['cons', rp('cons')],
        ['recommended_offer', rp('recommended_offer')],
        ['ranked_reasoning', rp('ranked_reasoning')],
      ].filter(([, v]) => v),
    )
    if (r.ranked_score != null) out.research.ranked_score = r.ranked_score
  }
  // Drop empty strings so the model reads what is known, not a wall of "".
  for (const k of Object.keys(out)) {
    if (out[k] === '' || (Array.isArray(out[k]) && !out[k].length)) delete out[k]
  }
  return out
}

/**
 * The leads a question is about, ranked. Returns [] for a question with no
 * lead-shaped tokens (small talk, general questions) so nothing is attached.
 *
 * `rows` is every lead the browser holds (all categories, manual ones too);
 * `limit` bounds how many records ride to the model. Ties break by score
 * column, then name, so the same question always retrieves the same records.
 */
export function retrieveLeads(rows, question, { limit = 5 } = {}) {
  const q = norm(question)
  const toks = tokens(question)
  if (!toks.length || !Array.isArray(rows) || !rows.length) return []
  const scored = []
  for (const row of rows) {
    if (!row || !row.name) continue
    const { score, hits } = scoreRow(row, q, toks)
    if (score > 0) scored.push({ row, score, hits })
  }
  // A lone weak hit (one generic token somewhere on the record) is noise —
  // "chicago" alone matches four hundred rows. Keep records that matched on a
  // name, a contact or an email, or on at least two tokens.
  const strong = scored.filter(
    (s) => s.score >= 6 || s.hits.length >= 2,
  )
  strong.sort(
    (a, b) =>
      b.score - a.score ||
      (Number(b.row.score) || 0) - (Number(a.row.score) || 0) ||
      String(a.row.name).localeCompare(String(b.row.name)),
  )
  // Under a strong top hit (the company or a person named outright), a row
  // that merely shares one word ("masonry") is noise, not a candidate — it
  // would only invite the model to talk about the wrong company. Keep rows
  // within reach of the best score; a vague question (every row weak) keeps
  // its whole shortlist.
  const best = strong.length ? strong[0].score : 0
  const cutoff = Math.max(6, best * 0.3)
  const kept = strong.filter((s) => s.score >= cutoff)
  return kept.slice(0, limit).map(({ row, score, hits }) => ({
    row,
    score,
    hits,
    facts: leadFacts(row, { research: getEnrichment(String(keyOf(row) || ''))?.result || null, hits }),
  }))
}
